// router/index.js
import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router'
import { getSystemPath, TWORK_SETTINGS } from '@/utils/helper'
// GTM is initialized in the CookieConsent component

// Helper function to load template components with a fallback
function loadTemplateComponent(componentName) {
  return () => {
    // Read the template configuration at runtime, not at module load time.
    const template = window.vueAppConfig?.settings?.template ?? 'default'
    return import(`@/components/template/${template}/${componentName}.vue`)
      .catch(() => import(`@/components/default/${componentName}.vue`))
  }
}

const routes = [
  {
    path: `/${getSystemPath('login')}`,
    name: 'Login',
    component: loadTemplateComponent('LoginPage')
  },
  {
    path: `/${getSystemPath('register')}`,
    name: 'Register',
    component: loadTemplateComponent('RegisterPage')
  },
  {
    path: `/${getSystemPath('forgotPassword')}`,
    name: 'ForgotPassword',
    component: loadTemplateComponent('ForgottenPage')
  },
  {
    path: `/${getSystemPath('account')}/:id`,
    name: 'UserAccount',
    component: loadTemplateComponent('UserAccount'),
    props: true
  },
  {
    path: `/${getSystemPath('legal')}/:id`,
    name: 'LegalPages',
    component: loadTemplateComponent('LegalPages'),
    props: true
  },
  {
    path: `/:type/:id/${getSystemPath('join')}`,
    name: 'EventJoin',
    component: loadTemplateComponent('EventJoin'),
    props: true
  },
  {
    path: `/:type/:id/${getSystemPath('pay')}`,
    name: 'EventPay',
    component: loadTemplateComponent('EventPay'),
    props: true
  },
  {
    path: '/:type/:id',
    name: 'EventPage',
    component: loadTemplateComponent('EventPage'),
    props: true
  },
  {
    path: `/${getSystemPath('lecturer')}/:lecturerSlug/:tabId?`,
    name: 'LecturerPage',
    component: loadTemplateComponent('LecturerPage'),
    props: true
  },
  {
    path: `/:type/${getSystemPath('category')}/:categorySlug`,
    name: 'EventListCategory',
    component: loadTemplateComponent('EventList'),
    props: true
  },
  {
    path: `/${getSystemPath('category')}/:categorySlug`,
    name: 'EventListDefaultCategory',
    component: loadTemplateComponent('EventList'),
    props: true
  },
  {
    path: '/:type',
    name: 'EventListType',
    component: loadTemplateComponent('EventList')
  },
  {
    path: '/',
    name: 'EventList',
    component: loadTemplateComponent('EventList')
  },
  {
    path: '/:catchAll(.*)',
    name: 'NotFound',
    component: () => import('@/components/general/NotFound.vue'),
    beforeEnter: (to, from, next) => {
      console.log('Matching /:catchAll route', to.params)
      next()
    }
  }
]

// Determine history mode based on settings (hash or HTML5 history)
const settings = window.vueAppConfig?.settings || {}
const historyMode = settings.hash_mode
  ? createWebHashHistory()
  : createWebHistory(settings.base_path ? `/${settings.base_path}` : '')

const router = createRouter({
  history: historyMode,
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 }
  }
})

// We no longer need the afterEach hook for tracking page views
// GTM plugin will handle this automatically with router integration
// The router instance will be passed to GTM when cookie consent is given

// Global navigation guard that checks every URL for a redirect match (by path only)
router.beforeEach((to, from, next) => {
  if (Array.isArray(TWORK_SETTINGS.redirects)) {
    const currentPath = to.path

    const redirectRule = TWORK_SETTINGS.redirects.find(rule => {
      return (
        rule.source &&
        rule.source.path &&
        rule.target &&
        rule.target.url &&
        rule.source.path === currentPath
      )
    })

    if (redirectRule) {
      // Perform a full browser redirect to the target URL
      window.location.href = redirectRule.target.url
      return // Stop further navigation within Vue Router
    }
  }
  next()
})

export function createAppRouter() {
  return router
}

export default router
